import { mixgenieServiceURL } from '../redux/services/defines';
import { clearReferralCode, readReferralCode } from './referralStore';

/**
 * FE half of the referral flow. `attributeIfPending` is called by both
 * signup paths (native register + Cognito callback) after user creation.
 * It's a no-op if no code was captured. It always clears the stored
 * code — even on "blocked" server responses (self-referral, exhausted,
 * disposable email etc) — so we don't re-try attribution on a stale
 * code across sessions.
 *
 * Deliberately fire-and-forget: signup MUST NOT fail because a referral
 * attribution failed. Errors are logged (console.warn) for diagnostics
 * and swallowed otherwise.
 */
export async function attributeIfPending(refereeUserId: string): Promise<void> {
  const code = readReferralCode();
  if (!code || !refereeUserId) return;
  try {
    const url = `${mixgenieServiceURL.replace('/gateway/mixgenie-service', '/public-gateway/mixgenie-service')}/referrals/attribute`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, refereeUserId }),
    });
    if (!res.ok) {
      console.warn(`[referrals] attribute HTTP ${res.status}`);
    }
  } catch (err: any) {
    console.warn(`[referrals] attribute failed (non-blocking): ${err?.message}`);
  } finally {
    clearReferralCode();
  }
}

export interface ReferralMe {
  code: string;
  active: boolean;
  maxUses: number;
  usedCount: number;
  stats: { qualified: number; granted: number; totalCreditsEarned: number };
  config: {
    enabled: boolean;
    signupRewardReferrer: number;
    signupRewardReferee: number;
    firstPurchaseRewardReferrer: number;
  };
}

/**
 * Fetch (or lazily create) the current user's code + stats. Requires an
 * authenticated session. Returns null when the server returns 403
 * (referrals disabled for this app) so the caller can hide the whole
 * "Invite friends" surface gracefully rather than showing an error.
 */
export async function fetchMyReferral(): Promise<ReferralMe | null> {
  try {
    const url = `${mixgenieServiceURL}/referrals/me`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.data || null;
  } catch {
    return null;
  }
}
